<script setup lang="ts">
import { ChordPreferences } from "@/models/ChordPreferences";
import { FontSetting } from "@/models/FontSetting";
import { LoadStatus } from "@/models/frontend-only/LoadStatus";
import { WatchHandle } from "vue";

const store = useSongStore();
const busy = useBusyStore();
const api = useApiStore();
const toast = useToastStore();
const app = useAppStore();
const pdf = usePDFStore();
const profile = useProfileStore();

const { t } = useI18n();

const defaultKey = computed(() => store.defaultKey || "A");
const key = ref<string>(defaultKey.value);
const chartType = useLocalStorage<string>(
  AppConstants.LOCALSTORAGE_KEYS.CHORDS_CHARTTYPE,
  "Standard",
) as globalThis.Ref<string>;
const twoColMode = useLocalStorage<boolean>(
  AppConstants.LOCALSTORAGE_KEYS.CHORDS_TWOCOLMODE,
  false,
) as globalThis.Ref<boolean>;
const htmlChords = ref("");
const shareStore = useShareStore();
const songNumber = computed(() => store.data?.ccliSongNumber || "");
const status = ref<LoadStatus>(LoadStatus.Loading);

const headingsFontFamily = computed(() => {
  let family = AvailableFontFamilies[0];
  if (profile.profile?.chordPreferences.fontSettings.headings.family) {
    family =
      AvailableFontFamilies.find(
        (f) =>
          f.type ===
          profile.profile!.chordPreferences.fontSettings.headings.family,
      ) || AvailableFontFamilies[0];
  }
  loadFont(family.id, family.url);
  return family;
});

const lyricsFontFamily = computed(() => {
  let family = AvailableFontFamilies[0];
  if (profile.profile?.chordPreferences.fontSettings.lyrics.family) {
    family =
      AvailableFontFamilies.find(
        (f) =>
          f.type ===
          profile.profile!.chordPreferences.fontSettings.lyrics.family,
      ) || AvailableFontFamilies[0];
  }
  loadFont(family.id, family.url);
  return family;
});

const directionsFontFamily = computed(() => {
  let family = AvailableFontFamilies[0];
  if (profile.profile?.chordPreferences.fontSettings.directions.family) {
    family =
      AvailableFontFamilies.find(
        (f) =>
          f.type ===
          profile.profile!.chordPreferences.fontSettings.directions.family,
      ) || AvailableFontFamilies[0];
  }
  loadFont(family.id, family.url);
  return family;
});

const chordsFontFamily = computed(() => {
  let family = AvailableFontFamilies[0];
  if (profile.profile?.chordPreferences.fontSettings.chords.family) {
    family =
      AvailableFontFamilies.find(
        (f) =>
          f.type ===
          profile.profile!.chordPreferences.fontSettings.chords.family,
      ) || AvailableFontFamilies[0];
  }
  loadFont(family.id, family.url);
  return family;
});
const fontSettings = useLocalStorage<{
  headings: FontSetting;
  directions: FontSetting;
  chords: FontSetting;
  lyrics: FontSetting;
}>(AppConstants.LOCALSTORAGE_KEYS.CHORDS_FONTSETTINGS, {
  headings: {
    size: "16",
    color: AvailableTextColors[0].name,
    highlight: AvailableHighlightColors[0].name,
    family: AvailableFontFamilies[0].type,
  },
  directions: {
    size: "16",
    color: AvailableTextColors[0].name,
    highlight: AvailableHighlightColors[0].name,
    family: AvailableFontFamilies[0].type,
  },
  chords: {
    size: "16",
    color: AvailableTextColors[0].name,
    highlight: AvailableHighlightColors[0].name,
    family: AvailableFontFamilies[0].type,
  },
  lyrics: {
    size: "16",
    color: AvailableTextColors[0].name,
    highlight: AvailableHighlightColors[0].name,
    family: AvailableFontFamilies[0].type,
  },
});

const fontSize = ref("16");

const filename = computed(
  () => sanitizeFilename(store.song?.title || "") + "-chords-" + key.value,
);

const showBusy = ref({
  copy: false,
  print: false,
  download: false,
  share: false,
});

const shownBusy = computed(() => ({
  email: store.emailSettings.busy,
  ...showBusy.value,
}));

function getPdfUrl(
  activityType: "shared" | "downloaded" | "printed" | "previewed",
) {
  const searchParams = new URLSearchParams({
    songNumber: songNumber.value,
    key: key.value,
    style: chartType.value,
    columns: twoColMode.value ? "2" : "1",
    activityType: activityType,
    enableFontCustomization: enableFontSettingsFeature.value ? "1" : "0",
    ...(enableFontSettingsFeature.value
      ? {
          css: getCSSForChordSheet(
            fontSize.value,
            fontSettings.value,
            false,
            true,
          ),
        }
      : {}),
  });
  const url = `/api/GetSongChordsPdf?${searchParams.toString()}`;
  return url;
}

const usedMaxSongsChords = computed(
  () => store.song?.products.chords.exceededMaxUniqueSongCount || false,
);

const usedMaxSongsChordPro = computed(
  () => store.song?.products.chordPro.exceededMaxUniqueSongCount || false,
);

function usedMaxSongs(product: string) {
  if (product === "chords") {
    return usedMaxSongsChords.value;
  } else if (product === "chordpro") {
    return usedMaxSongsChordPro.value;
  }
  return false;
}

const init = ref(false);
let watchHandle: WatchHandle | null = null;

async function initWhenSongIsLoaded() {
  await waitUntilTrue(
    () =>
      store.status === LoadStatus.Ready &&
      store.song &&
      store.defaultKey &&
      profile.profile,
  );
  key.value = store.defaultKey;
  await getSongSheet();
  init.value = true;

  watchHandle = watch(
    () => [songNumber.value, key.value, chartType.value, twoColMode.value],
    getSongSheet,
  );
}

const loading = computed(() => store.loading);

watch(loading, (loading) => {
  if (!loading) {
    reset();
  }
});

let saveWatchHandle: WatchHandle | null = null;
onMounted(async () => {
  await getProfileSettings();
  await initWhenSongIsLoaded();
  saveWatchHandle = watch(
    () => [fontSettings, fontSize, twoColMode, chartType],
    debouncedSavedSetting,
    {
      deep: true,
    },
  );
});

function reset() {
  chartType.value = "Standard";
  key.value = defaultKey.value;
  twoColMode.value = false;
  fontSize.value = "16";

  fontSettings.value = {
    headings: {
      size: "16",
      color: AvailableTextColors[0].name,
      highlight: AvailableHighlightColors[0].name,
      family: AvailableFontFamilies[0].type,
    },
    lyrics: {
      size: "16",
      color: AvailableTextColors[0].name,
      highlight: AvailableHighlightColors[0].name,
      family: AvailableFontFamilies[0].type,
    },
    directions: {
      size: "16",
      color: AvailableTextColors[0].name,
      highlight: AvailableHighlightColors[0].name,
      family: AvailableFontFamilies[0].type,
    },
    chords: {
      size: "16",
      color: AvailableTextColors[0].name,
      highlight: AvailableHighlightColors[0].name,
      family: AvailableFontFamilies[0].type,
    },
  };
}

async function print() {
  showBusy.value.print = true;
  const url = getPdfUrl("printed");
  try {
    await pdf.printPdf(url);
  } catch (err) {
    toast.errorToast("SV.LIT_PrintErrorHasOccurred");
    throw err;
  }
  showBusy.value.print = false;
}

async function share() {
  if (!store.song) {
    return;
  }
  showBusy.value.share = true;

  await shareStore.fetchUrlPdfAndShare(
    getPdfUrl("shared"),
    filename.value + ".pdf",
    store.song.title,
    t("G.LIT_ChordSheet"),
  );
  showBusy.value.share = false;
}

async function getSongSheet() {
  if (!songNumber.value) {
    return;
  }

  busy.showBusy();
  status.value = LoadStatus.Loading;
  try {
    htmlChords.value = "";

    htmlChords.value = await api.get("/GetSongChordsHTML", {
      songNumber: songNumber.value,
      key: key.value,
      style: chartType.value,
      columns: twoColMode.value ? "2" : "1",
      enableFontCustomization: enableFontSettingsFeature.value ? "1" : "0",
    });

    status.value = LoadStatus.Ready;
  } catch (err) {
    status.value = LoadStatus.Error;
    throw err;
  }
  busy.hideBusy();
}

async function download() {
  if (!songNumber.value || !store.song) {
    return;
  }
  showBusy.value.download = true;

  await downloadUrl(getPdfUrl("downloaded"), filename.value + ".pdf");
  showBusy.value.download = false;
}

async function downloadChordPro() {
  if (!songNumber.value || !store.song) {
    return;
  }
  showBusy.value.download = true;

  try {
    const chordpro = await api.get("/GetSongChordPro", {
      songNumber: songNumber.value,
      key: key.value,
      style: chartType.value,
      columns: twoColMode.value ? "2" : "1",
    });

    await downloadTextFile(
      chordpro,
      store.song.slug + "-chordpro-" + key.value,
    );
  } catch (err) {
    showBusy.value.download = false;
    throw err;
  }
  showBusy.value.download = false;
}

const showPlaceholders = computed(
  () =>
    store.status === LoadStatus.Loading ||
    store.status === LoadStatus.Error ||
    status.value === LoadStatus.Loading ||
    status.value === LoadStatus.Error ||
    (store.status === LoadStatus.Ready &&
      !store.song?.products.chords.authorized),
);

function emailSong() {
  store.emailSong({
    product: "chords",
    songKey: key.value,
    style: chartType.value,
    columns: twoColMode.value ? "2" : "1",
    noteSize: null,
    octave: null,
    orientation: null,
    paperSize: null,
    enableFontCustomization: enableFontSettingsFeature.value,
    ...(enableFontSettingsFeature.value
      ? {
          css: getCSSForChordSheet(
            fontSize.value,
            fontSettings.value,
            false,
            true,
          ),
        }
      : {}),
  });
}
const beta = useBetaStore();
const enableFontSettingsFeature = computed(
  () => beta.features.chordFontSettings,
);

const debouncedSavedSetting = useDebounceFn(saveSetting, 500);

async function getProfileSettings() {
  await waitUntilTrue(() => !!profile.profile);

  fontSettings.value = {
    headings: {
      size:
        profile.profile?.chordPreferences.fontSettings?.headings.size || "16",
      color:
        profile.profile?.chordPreferences.fontSettings?.headings.color ||
        AvailableTextColors[0].name,
      highlight:
        profile.profile?.chordPreferences.fontSettings?.headings.highlight ||
        AvailableHighlightColors[0].name,
      family: headingsFontFamily.value.type,
    },
    directions: {
      size:
        profile.profile?.chordPreferences.fontSettings?.directions.size || "16",
      color:
        profile.profile?.chordPreferences.fontSettings?.directions.color ||
        AvailableTextColors[0].name,
      highlight:
        profile.profile?.chordPreferences.fontSettings?.directions.highlight ||
        AvailableHighlightColors[0].name,
      family: directionsFontFamily.value.type,
    },
    chords: {
      size: profile.profile?.chordPreferences.fontSettings?.chords.size || "16",
      color:
        profile.profile?.chordPreferences.fontSettings?.chords.color ||
        AvailableTextColors[0].name,
      highlight:
        profile.profile?.chordPreferences.fontSettings?.chords.highlight ||
        AvailableHighlightColors[0].name,
      family: chordsFontFamily.value.type,
    },
    lyrics: {
      size: profile.profile?.chordPreferences.fontSettings?.lyrics.size || "16",
      color:
        profile.profile?.chordPreferences.fontSettings?.lyrics.color ||
        AvailableTextColors[0].name,
      family: lyricsFontFamily.value.type,
    },
  };

  fontSize.value =
    profile.profile?.chordPreferences.fontSize.toString() || "16";
  twoColMode.value = profile.profile?.chordPreferences.columns === "2" || false;
  chartType.value = profile.profile?.chordPreferences.style || "Standard";
}

function saveSetting() {
  if (profile.profile) {
    const settings: ChordPreferences = {
      fontSize: parseInt(fontSize.value),
      style: chartType.value,
      columns: twoColMode.value ? "2" : "1",
      fontSettings: fontSettings.value,
    };
    api.post("/UpdateChordPreferences", {
      chordPreferences: JSON.stringify(settings),
    });
    profile.profile.chordPreferences = settings;
  }
}

onUnmounted(() => {
  watchHandle?.stop();
  saveWatchHandle?.stop();
});
</script>

<template>
  <PageWrapper no-body>
    <FullScreenWrapper
      :class="{ 'placeholder-glow': status == LoadStatus.Loading }"
    >
      <template #header>
        <SongDetailsAlertContentError
          v-if="status === LoadStatus.Error"
          @btnclick="getSongSheet"
        />
        <SongDetailsAlertNotAuthenticated
          v-else-if="
            !store.placeholderNotLoaded &&
            store.data?.products.chords.noAuthReason ===
              NoAuthReason.NotAuthenticated
          "
        />
        <SongDetailsAlertUpgradeSubscription
          v-else-if="
            !store.placeholderNotLoaded &&
            store.data?.products.chords.noAuthReason ===
              NoAuthReason.RequiresSubscriptionUpgrade
          "
        />
      </template>

      <MusicSheetChordsPlaceholder v-if="showPlaceholders" />
      <MusicSheetChords
        v-else
        :html="htmlChords"
        :font-settings="fontSettings"
        :font-size="fontSize"
      />

      <template #action-bar-controls>
        <div
          class="ga-info"
          :data-songnumber="store.song?.ccliSongNumber"
          :data-song-selectedkey="key"
          :data-content-type="'ChordSheet'"
          :data-chordstyle="chartType"
          :data-layoutstyle="twoColMode ? 'TwoColumn' : 'SingleColumn'"
        >
          <ToolbarActionBarControls
            enable-email
            enable-share
            :enable-print="!isFirefox()"
            dropdown-download
            product="chords"
            :show-busy="shownBusy"
            :max-song-usage-check="usedMaxSongs"
            :ids="{
              copy: 'chordSheetCopyButton',
              download: 'chordSheetDownloadDropdown',
              downloadChordPro: 'chordSheetDownloadChordProButton',
              downloadPDF: 'chordSheetDownloadButton',
              email: 'chordSheetEmailButton',
              fullscreen: 'chordSheetFullscreenButton',
              print: 'chordSheetPrintButton',
              share: 'chordSheetShareButton',
            }"
            @print="print"
            @download="download"
            @download-chord-pro="downloadChordPro"
            @share="share"
            @email="emailSong"
          />
        </div>
      </template>
      <template #toolbar>
        <ToolbarSelectChordsTransposeKey
          id="ChordSheetTransposeKeySelectInput"
          v-model="key"
          :default-key="defaultKey"
        />

        <ToolbarSelectChartType v-model="chartType" />
        <ToolbarColumns v-model="twoColMode" />
        <template v-if="enableFontSettingsFeature">
          <ToolbarFontSize
            v-model="fontSize"
            chord-sheet
            :title="$t('SV.LIT_Scale')"
          />

          <ToolbarFontSettings
            v-model="fontSettings"
            enable-chords
          />

          <ToolbarReset @reset="reset" />
        </template>
      </template>
    </FullScreenWrapper>
  </PageWrapper>
</template>

<style scoped lang="postcss"></style>
